import { useState, useEffect } from 'react'
import { supabase } from './lib/supabase'

// Pages
import AuthPage from './pages/AuthPage'
import MapPage from './pages/MapPage'
import BookingsPage from './pages/BookingsPage'
import ChargersPage from './pages/ChargersPage'
import ProfilePage from './pages/ProfilePage'
import OnboardingPage from './pages/OnboardingPage'

// Shared
import BottomNav from './components/shared/BottomNav'
import Toast from './components/shared/Toast'

const App = () => {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('kaart')
  const [toast, setToast] = useState(null)
  const [mapFocus, setMapFocus] = useState(null)
  const [showAuth, setShowAuth] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [authReturnAction, setAuthReturnAction] = useState(null)

  useEffect(() => {
    // Check session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)

      // Show onboarding for first time visitors
      const seen = localStorage.getItem('buurtlader_onboarding_seen')
      if (!seen) {
        setShowOnboarding(true)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session)
        if (session) {
          setShowAuth(false)
          // Return to action after login if needed
          if (authReturnAction) {
            authReturnAction()
            setAuthReturnAction(null)
          }
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [authReturnAction])

  const showToast = (message, error = false) => {
    setToast({ message, error })
    setTimeout(() => setToast(null), 3000)
  }

  const requireAuth = (action) => {
    if (session) {
      action()
    } else {
      setAuthReturnAction(() => action)
      setShowAuth(true)
    }
  }

  const handleTabChange = (tab) => {
    if (!session && tab !== 'kaart') {
      setShowAuth(true)
      return
    }
    setActiveTab(tab)
  }

  if (loading) return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--cream)',
      fontFamily: 'DM Sans, sans-serif', color: 'var(--green)',
      fontSize: 18, fontWeight: 600
    }}>
      Buurtlader laden...
    </div>
  )

  // Onboarding for first time visitors
  if (showOnboarding) return (
    <OnboardingPage onDone={() => {
      localStorage.setItem('buurtlader_onboarding_seen', 'true')
      setShowOnboarding(false)
    }} />
  )

  // Auth screen - shown when login is required
  if (showAuth) return (
    <AuthPage
      showToast={showToast}
      onBack={() => setShowAuth(false)}
    />
  )

  const renderPage = () => {
    switch (activeTab) {
      case 'kaart':
        return (
          <MapPage
            session={session}
            showToast={showToast}
            setActiveTab={setActiveTab}
            mapFocus={mapFocus}
            requireAuth={requireAuth}
          />
        )
      case 'boekingen':
        return (
          <BookingsPage
            session={session}
            showToast={showToast}
          />
        )
      case 'laadpalen':
        return (
          <ChargersPage
            session={session}
            showToast={showToast}
            setActiveTab={setActiveTab}
            setMapFocus={setMapFocus}
          />
        )
      case 'profiel':
        return (
          <ProfilePage
            session={session}
            showToast={showToast}
            onShowOnboarding={() => setShowOnboarding(true)}
            onLogout={() => {
              setActiveTab('kaart')
            }}
          />
        )
      default:
        return (
          <MapPage
            session={session}
            showToast={showToast}
            setActiveTab={setActiveTab}
            mapFocus={mapFocus}
            requireAuth={requireAuth}
          />
        )
    }
  }

  return (
    <div style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      background: 'var(--cream)', fontFamily: 'DM Sans, sans-serif',
      overflow: 'hidden'
    }}>
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {renderPage()}
      </div>
      <BottomNav
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        session={session}
        onLoginClick={() => setShowAuth(true)}
      />
      {toast && <Toast toast={toast} />}
    </div>
  )
}

export default App
